import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../../../common/infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import {
  IInventoryItemSearchResultModel,
  IInventoryTransferLineFormModel,
} from "../models/inventoryTransferModels";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { getInventoryItemDisplayName } from "../../../../common/utilities/inventoryDisplayUtility";

interface IUseProductSearchArgs {
  /** Called when the user picks exactly one product (auto-add or suggestion click). */
  onProductPicked: (product: IInventoryItemSearchResultModel) => void;
  disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Owns the product picker search state for a transfer dialog:
 *   - Typed query with debounced backend search
 *   - Auto-add on exact barcode match (single result from backend)
 *   - Feeds suggestions to SuggestInputField
 *   - Exposes a quick-add opener flag for the "+ Нов продукт" dialog
 *
 * The picker input is not part of the submit payload — it lives in local
 * component state and is cleared after each successful pick.
 */
export const useInventoryTransferProductSearch = ({
  onProductPicked,
  disabled,
}: IUseProductSearchArgs) => {
  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState<IInventoryItemSearchResultModel[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequestIdRef = useRef(0);

  const runSearch = useCallback(async (query: string): Promise<IInventoryItemSearchResultModel[]> => {
    const requestId = ++latestRequestIdRef.current;
    try {
      const response = await axiosInstance.get<IInventoryItemSearchResultModel[]>(
        `${endpointsMetadata.inventoryItemNomenclature.searchProducts}?query=${encodeURIComponent(query)}`,
      );
      if (requestId !== latestRequestIdRef.current) return [];
      return response.data ?? [];
    } catch {
      if (requestId === latestRequestIdRef.current) {
        getErrorToast("Неуспешно търсене на продукти.");
      }
      return [];
    }
  }, []);

  // Debounced typing → populate dropdown suggestions.
  useEffect(() => {
    if (disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchValue || searchValue.trim().length === 0) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const fetched = await runSearch(searchValue.trim());
      setResults(fetched);
      setHasSearched(true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue, disabled, runSearch]);

  /** USB scanner / Enter / camera scan → immediate single lookup. */
  const handleBarcodeScanned = useCallback(
    async (value: string) => {
      if (disabled) return;
      const trimmed = value?.trim();
      if (!trimmed) return;

      const fetched = await runSearch(trimmed);
      if (fetched.length === 1) {
        onProductPicked(fetched[0]);
        setSearchValue("");
        setResults([]);
      } else if (fetched.length === 0) {
        getErrorToast("Продуктът не беше намерен.");
      } else {
        // Multiple matches → surface as suggestions for the user to pick.
        setResults(fetched);
      }
    },
    [disabled, runSearch, onProductPicked],
  );

  const handleSuggestionSelect = useCallback(
    (selectedId: number) => {
      const picked = results.find((r) => r.id === selectedId);
      if (!picked) return;
      onProductPicked(picked);
      setSearchValue("");
      setResults([]);
    },
    [results, onProductPicked],
  );

  /** Suggestions shape expected by SuggestInputField (name+id only). */
  const suggestionsForField: ISelectFieldModel[] = results.map((r) => {
    const displayName = getInventoryItemDisplayName(r.name, r.brand);
    return {
      id: r.id,
      name: r.emoji ? `${r.emoji} ${displayName}` : displayName,
    };
  });

  const openQuickAdd = useCallback(() => setIsQuickAddOpen(true), []);
  const closeQuickAdd = useCallback(() => setIsQuickAddOpen(false), []);

  /** Called when the quick-add flow successfully creates a new product. */
  const handleQuickAddSuccess = useCallback(
    (created: IInventoryItemSearchResultModel) => {
      onProductPicked(created);
      setSearchValue("");
      setResults([]);
      setIsQuickAddOpen(false);
    },
    [onProductPicked],
  );

  return {
    searchValue,
    setSearchValue,
    suggestionsForField,
    hasSearched,
    handleBarcodeScanned,
    handleSuggestionSelect,
    isQuickAddOpen,
    openQuickAdd,
    closeQuickAdd,
    handleQuickAddSuccess,
    // Raw results (rarely needed, but exposed for debugging/testing).
    results,
  };
};

/**
 * Returns true if the form line list contains the given product already
 * (used for UI nudges, not validation — duplicates are allowed).
 */
export const hasProductInLines = (
  lines: IInventoryTransferLineFormModel[],
  productId: number,
) => lines.some((l) => l.inventoryItemId === productId);
